import * as React from 'react';
import { flatten, get, isEmpty, isNaN, isNil, omit } from 'lodash';
import { createReducer } from 'utils/redux';
import { API, destroy, get as getRequest, post } from 'utils/api';
import { snakeCaseKeys } from 'utils/keys';
import { logError } from 'utils/log';
import { Querystring } from 'utils/url';
import { API_CODE_CONTENT_TYPE_IS_NOT_SUPPORTED, NOT_FOUND_STATUS_CODE } from 'utils/status';

import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import {
  deliverableAttachmentAdded,
  deliverableAttachmentRemoved
} from '../../subcampaigns/reducers/offerDeliverables';
import {
  metricsAttachmentAdded,
  metricsAttachmentRemoved
} from 'modules/campaigns/components/campaign-report/metrics/reducers/campaign-report-metrics';
import {
  campaignDeliverableAttachmentAdded,
  campaignDeliverableAttachmentRemoved
} from 'modules/campaigns/reducers/campaignDeliverables';
import { resetUploadProgress, setUploadProgress } from 'redux-store/reducers/uploadProgress';
import { initCloudinaryStreaming } from 'redux-store/reducers/cloudinaryVideos';

import { trackAnalyticScreenshotOverride } from 'modules/tracking/utils/tracking';
import { addTempIds, uploadImages as uploadImagesPdfs, uploadVideos } from 'utils/upload-asset';

import {
  DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS,
  DELIVERABLE_ATTACHMENT_TYPE_HIGHRES,
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  buildPrefixForDownloadableAttachment,
  isInstagramPost,
  isInstagramReel,
  isInstagramStory,
  validateFile
} from 'utils/deliverables';
import { downloadIndividualFile, waitUntilZipIsReadyAndGetPresignedURL } from 'utils/file';
import { __t, applyTranslations } from 'utils/language';
import { uploadCloudinaryVideo } from 'utils/cloudinary-video-upload';
import { uploadCollabaryVideo } from 'utils/collabary-video-upload';

import type {
  Action,
  AssetMediaFormat,
  Deliverable,
  DeliverableAttachment,
  DeliverableAttachmentType,
  DeliverableType,
  Dispatch, GetState,
  LambdaProxyResponse,
  SubcampaignInfluencerProfile,
  ThunkAction
} from 'types';
import { ApiUploadEvent } from 'types';

type AttachmentInfo = {
  isInvalidImage: boolean,
  tmpAttachment?: DeliverableAttachment,
  parsedMetrics?: any
};

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./deliverable-media-attachments.i18n.json'));

const RESET_UPLOAD_STATE = 'deliverableMediaAttachments/RESET_UPLOAD_STATE';
const SHOW_ERROR = 'deliverableMediaAttachments/SHOW_ERROR';
const CLEAN_ERRORS = 'deliverableMediaAttachments/CLEAN_ERRORS';
const START_UPLOADING = 'deliverableMediaAttachments/START_UPLOADING';
const FINISH_UPLOADING = 'deliverableMediaAttachments/FINISH_UPLOADING';

const MAX_FILE_SIZE_TO_VALIDATE = 5 * 1024 * 1024; // 5MB

const { CAMPAIGNS, DELIVERABLE_MEDIA } = API.END_POINTS;

const initialState = {
  uploadingAttachmentsByDeliverableId: {},
  errorsByDeliverableId: {}
};

export function resetUploadState(deliverableId: number): Action {
  return {
    type: RESET_UPLOAD_STATE,
    payload: {
      deliverableId
    }
  };
}

export function cleanUploadErrors(deliverableId: number): Action {
  return {
    type: CLEAN_ERRORS,
    payload: {
      deliverableId
    }
  };
}

export function showUploadError(
  deliverableId: number,
  attachmentType: DeliverableAttachmentType,
  error: string
): Action {
  return {
    type: SHOW_ERROR,
    payload: {
      deliverableId,
      attachmentType,
      error
    }
  };
}

export function startUploadingDeliverableAttachment(deliverableId: number, tmpAttachment: DeliverableAttachment): Action {
  return {
    type: START_UPLOADING,
    payload: {
      deliverableId,
      tmpAttachment
    }
  };
}

export function finishUploadingDeliverableAttachment(deliverableId: number, tmpAttachment: DeliverableAttachment): Action {
  return {
    type: FINISH_UPLOADING,
    payload: {
      deliverableId,
      tmpAttachment
    }
  };
}

export function generateTmpAttachment(
  deliverableId: number,
  attachmentType: DeliverableAttachmentType,
  format: AssetMediaFormat
): DeliverableAttachment {
  return ({
    id: `uploading-attachment-${deliverableId}-${attachmentType}-${(new Date()).getTime()}-${Math.random()}`,
    assetType: format,
    meta: {
      type: attachmentType
    },
    url: ''
  });
}

function uploadImage(attachmentType: DeliverableAttachmentType, imageFile: File, onUploadProgress: (progressEvent: ApiUploadEvent) => void): Promise<Partial<DeliverableAttachment>> {
  const url = API.END_POINTS.API.IMAGES;
  const data = new FormData();

  data.append('file', imageFile);

  return post(url, { data, onUploadProgress })
    .then((response) => ({
      url: response.data.url,
      assetType: 'image',
      meta: {
        type: attachmentType
      },
      fileName: imageFile.name
    }));
}

function uploadVideo(
  attachmentType: DeliverableAttachmentType,
  videoFile: File,
  onUploadProgress: (progressEvent: ApiUploadEvent) => void,
  withCollabaryUpload: boolean
): Promise<DeliverableAttachment> {
  if (withCollabaryUpload) {
    return uploadCollabaryVideo(videoFile, onUploadProgress)
      .then(response => ({
        id: response.data.url,
        url: response.data.url,
        assetType: 'video',
        meta: {
          type: attachmentType,
          videoUploadResponse: response
        },
        fileName: videoFile.name
      }));
  }

  return uploadCloudinaryVideo(videoFile, onUploadProgress)
    .then(response => ({
      id: response.publicId,
      url: response.secureUrl,
      assetType: 'video',
      meta: {
        type: attachmentType
      },
      fileName: videoFile.name
    }));
}

export function uploadAssets(assets: Array<File>, noGlobalLoader?: boolean) {
  const url = API.END_POINTS.API.IMAGES;
  const imagesPdfs = assets.filter(asset => (asset.type.includes('image') || asset.type.includes('pdf')));
  const videos = assets.filter(asset => asset.type.includes('video'));
  const promises: Array<Promise<any>> = [];

  return (dispatch: Dispatch, getState: GetState) => {
    const { runtimeConfig } = getState();

    if (!noGlobalLoader) {
      dispatch(showLoading(url));
    }

    if (imagesPdfs.length) {
      promises.push(uploadImagesPdfs(imagesPdfs));
    }

    if (videos.length) {
      promises.push(uploadVideos(videos, Boolean(runtimeConfig.config.userS3VideoUpload)));
    }

    return Promise.all(promises)
      .then((uploadedAssets) => addTempIds(flatten(uploadedAssets)))
      .then((result) => {
        if (runtimeConfig.config.userS3VideoUpload) {
          dispatch(initCloudinaryStreaming(result));
        }
        return result;
      })
      .catch((error) => {
        logError(error);

        if (error?.response?.data?.code === API_CODE_CONTENT_TYPE_IS_NOT_SUPPORTED) {
          dispatch(danger(__t('collabary_common.errors.content_type_is_not_supported')));
        } else {
          dispatch(danger(error.message || __t('deliverable_media_attachments.task_inspirational_attachment_failed')));
        }
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

function postDeliverableAttachment(
  offerId: number,
  deliverableId: number,
  tmpAttachment: DeliverableAttachment,
  parsedMetrics: any,
  ignoreMetricsCreation?: boolean
): ThunkAction {
  const url = DELIVERABLE_MEDIA.addOfferAttachment(offerId, deliverableId, ignoreMetricsCreation);

  const data = {
    url: tmpAttachment.url,
    assetType: tmpAttachment.assetType,
    meta: {
      type: tmpAttachment.meta.type
    },
    fileName: tmpAttachment.fileName,
    parsedBeforeMetrics: (!parsedMetrics || isEmpty(parsedMetrics)) ? null : parsedMetrics
  };

  return () => post(url, { data })
    .then(response => response.data);
}

type ImageValidationType = 'instagram_post_analytics'
  | 'instagram_story_analytics'
  | 'instagram_reel_analytics'
  | 'tiktok_video_analytics'
  | 'influencer_instagram_audience_analytics'
  | 'influencer_tiktok_audience_analytics'
  | 'unknown';

export function validateDeliverableAttachment(tmpAttachment: { url: string }, assetType: ImageValidationType) {
  const url = DELIVERABLE_MEDIA.validateImageAttachment();

  return post(url, { data: { url: tmpAttachment.url, assetType } })
    .then(response => response.data)
    .catch(error => {
      // it can fail for big files
      logError(error);
      return null;
    });
}

function attachmentCreated(
  offerId: number,
  deliverableId: number,
  createdAttachment: DeliverableAttachment
): ThunkAction {
  return (dispatch) => {
    dispatch(deliverableAttachmentAdded(offerId, deliverableId, createdAttachment));
    dispatch(metricsAttachmentAdded(deliverableId, createdAttachment)); // Report metrics page
    dispatch(campaignDeliverableAttachmentAdded(deliverableId, createdAttachment)); // Brands Content page
  };
}

function assignDeliverableAttachment(
  offerId: number,
  deliverable: Deliverable,
  attachmentType: DeliverableAttachmentType,
  tmpAttachment: DeliverableAttachment,
  parsedMetrics?: any,
  ignoreMetricsCreation?: boolean
) {
  return async (dispatch) => {
    const deliverableId = deliverable.id;

    try {
      let createdAttachment: DeliverableAttachment;

      if (deliverable.deliverableType === DELIVERABLE_TYPE_CONTENT_PRODUCTION) {
        // don't create attachment entity yet
        createdAttachment = tmpAttachment;
      } else {
        createdAttachment = await dispatch(postDeliverableAttachment(
          offerId,
          deliverableId,
          tmpAttachment,
          parsedMetrics,
          ignoreMetricsCreation
        ));
      }

      dispatch(attachmentCreated(offerId, deliverableId, createdAttachment));
      dispatch(finishUploadingDeliverableAttachment(deliverableId, tmpAttachment));
    } catch (error) {
      logError(error);
      dispatch(showUploadError(deliverableId, attachmentType, __t('deliverable_media_attachments.file_upload_error')));
      dispatch(finishUploadingDeliverableAttachment(deliverableId, tmpAttachment));
    }
  };
}

export function uploadAndVerifyDeliverableAttachmentGeneric(
  deliverableId: number,
  deliverableType: DeliverableType,
  attachmentType: DeliverableAttachmentType,
  file: File,
  uploadId?: string,
  shouldStartUploading?: boolean | null
): ThunkAction {
  return async (dispatch: Dispatch, getState: GetState): Promise<AttachmentInfo | null> => {
    const { runtimeConfig } = getState();
    const validationError = validateFile(file, runtimeConfig.config.uploadAtttachmentMaxFileSize || undefined);

    if (validationError) {
      throw new Error(validationError);
    }

    const isImage = file.type.indexOf('image') >= 0 || file.type.indexOf('pdf') >= 0;
    let tmpAttachment = generateTmpAttachment(deliverableId, attachmentType, isImage ? 'image' : 'video');
    let uploadProgressId = uploadId + file.name;

    if (shouldStartUploading) {
      uploadProgressId = String(tmpAttachment.id);
      dispatch(startUploadingDeliverableAttachment(deliverableId, tmpAttachment));
    }


    try {
      const uploadedFile = isImage ?
        await uploadImage(attachmentType, file, (event) => {
          const percentsComplete = Math.round((event.loaded * 100) / event.total);
          dispatch(setUploadProgress(uploadProgressId, percentsComplete));
        }) :
        await uploadVideo(attachmentType, file, (event) => {
          const percentsComplete = Math.round((event.loaded * 100) / event.total);
          dispatch(setUploadProgress(uploadProgressId, percentsComplete));
        }, Boolean(runtimeConfig.config.userS3VideoUpload));

      dispatch(resetUploadProgress(uploadProgressId));

      tmpAttachment = {
        ...tmpAttachment,
        meta: {
          ...uploadedFile.meta,
          ...tmpAttachment.meta
        },
        url: uploadedFile.url ?? '',
        fileName: uploadedFile.fileName
      };

      if (runtimeConfig.config.userS3VideoUpload) {
        dispatch(initCloudinaryStreaming([tmpAttachment]));
      }

      let parsedMetrics: LambdaProxyResponse| null | undefined;

      const isTypeWithRecognition = isInstagramPost(deliverableType)
        || isInstagramStory(deliverableType)
        || isInstagramReel(deliverableType)
        || deliverableType === DELIVERABLE_TYPE_TIKTOK_VIDEO;

      if (
        isImage
        && isTypeWithRecognition
        && [DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES].indexOf(attachmentType) > -1
      ) {
        if (file.size > MAX_FILE_SIZE_TO_VALIDATE) {
          parsedMetrics = { assetType: 'unknown', metrics: {} };
        } else if (isInstagramPost(deliverableType)) {
          parsedMetrics = await validateDeliverableAttachment(tmpAttachment, 'instagram_post_analytics');
        } else if (isInstagramStory(deliverableType)) {
          parsedMetrics = await validateDeliverableAttachment(tmpAttachment, 'instagram_story_analytics');
        } else if (isInstagramReel(deliverableType)) {
          parsedMetrics = await validateDeliverableAttachment(tmpAttachment, 'instagram_reel_analytics');
        } else if (deliverableType === DELIVERABLE_TYPE_TIKTOK_VIDEO) {
          parsedMetrics = await validateDeliverableAttachment(tmpAttachment, 'tiktok_video_analytics');
        }

        if (isNil(parsedMetrics)) {
          dispatch(danger(__t('deliverable_media_attachments.broken_metric_error')));

          if (shouldStartUploading) {
            dispatch(finishUploadingDeliverableAttachment(deliverableId, tmpAttachment));
          }

          return null;
        }

        const imageSource = get(parsedMetrics, 'metrics.source', null);
        const isValidHighresImage = attachmentType === DELIVERABLE_ATTACHMENT_TYPE_HIGHRES && (!imageSource || imageSource === 'unknown');
        let isValidImage = isValidHighresImage;

        if (
          attachmentType === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS
          && (isInstagramPost(deliverableType) || isInstagramStory(deliverableType) || isInstagramReel(deliverableType))
        ) {
          const reachMetric: any = get(parsedMetrics, 'metrics.reach');
          const impressionsMetric: any = get(parsedMetrics, 'metrics.impressions');
          const hasParsedMetrics = reachMetric >= 0 && impressionsMetric >= 0;
          const isValidAnalyticsImage = attachmentType === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS && imageSource === `${deliverableType?.replace('_regram', '')}_analytics` && hasParsedMetrics;

          isValidImage = isValidImage || isValidAnalyticsImage;
        } else if (deliverableType === DELIVERABLE_TYPE_TIKTOK_VIDEO) {
          const audienceMetric = get(parsedMetrics, 'metrics.audienceCountriesPercent', []);
          const avgWatchTimeMetric = get(parsedMetrics, 'metrics.avgWatchTimeSec');
          const reachMetric = get(parsedMetrics, 'metrics.reach');
          const hasValidMetric = audienceMetric.length > 0 || !isNil(avgWatchTimeMetric) || !isNil(reachMetric);

          const isValidAnalyticsImage = attachmentType === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS && hasValidMetric;
          isValidImage = isValidImage || isValidAnalyticsImage;
        }

        if (!isValidImage) {
          // pause here
          // invalid image response to force `assign` later
          return {
            tmpAttachment,
            isInvalidImage: true,
            parsedMetrics
          };
        }
      }
      // proceed with assigning and finishing the attachment creation
      return {
        tmpAttachment,
        parsedMetrics,
        isInvalidImage: false
      };
    } catch (error) {
      logError(error);

      if (error?.response?.data?.code === API_CODE_CONTENT_TYPE_IS_NOT_SUPPORTED) {
        dispatch(danger(__t('collabary_common.errors.content_type_is_not_supported')));
      }

      if (shouldStartUploading) {
        dispatch(finishUploadingDeliverableAttachment(deliverableId, tmpAttachment));
      }
      throw new Error(__t('deliverable_media_attachments.file_upload_error'));
    }
  };
}

function uploadAndVerifyDeliverableAttachment(
  file: File,
  deliverable: Deliverable,
  attachmentType: DeliverableAttachmentType,
  offerId?: number,
  uploadId?: string,
  isNewDeliverable?: boolean,
  ignoreMetricsCreation?: boolean
) {
  return async (dispatch, getState) => {
    const { runtimeConfig } = getState();

    const deliverableId: number = deliverable.id;
    const validationError = validateFile(file, runtimeConfig.config.uploadAtttachmentMaxFileSize || undefined);

    try {
      if (validationError) {
        throw new Error(validationError);
      }

      const attachmentInfo = await dispatch(uploadAndVerifyDeliverableAttachmentGeneric(
        deliverableId,
        deliverable.deliverableType,
        attachmentType,
        file,
        uploadId,
        !isNewDeliverable
      ));

      if (attachmentInfo === null) {
        return null;
      }

      if (attachmentInfo.isInvalidImage) {
        return attachmentInfo;
      }

      // otherwise we have attachmentInfo -> tmpAttachment + parsedMetrics
      // proceed with assigning and finishing the attachment creation or
      // with new flow return attachment info and handle inside main component
      if (isNewDeliverable || !offerId) {
        return attachmentInfo;
      }

      await dispatch(
        assignDeliverableAttachment(
          offerId,
          deliverable,
          attachmentInfo.attachmentType,
          attachmentInfo.tmpAttachment,
          attachmentInfo.parsedMetrics,
          ignoreMetricsCreation
        )
      );
    } catch (error) {
      if (isNewDeliverable) {
        return error;
      }

      dispatch(showUploadError(deliverableId, attachmentType, error));
    }

    return null;
  };
}

export function createDeliverableAttachments(
  offerId: number,
  deliverable: Deliverable,
  attachmentType: DeliverableAttachmentType,
  files: Array<File>,
  feedbackModal: React.ReactNode,
  ignoreMetricsCreation?: boolean
) {
  return async (dispatch: Dispatch) => {
    const deliverableId = deliverable.id;

    dispatch(cleanUploadErrors(deliverableId));

    const uploadedFileResponses = await Promise.all(
      files.map(file => dispatch(uploadAndVerifyDeliverableAttachment(
        file,
        deliverable,
        attachmentType,
        offerId,
        undefined,
        undefined,
        ignoreMetricsCreation
      )))
    );

    const invalidImageResponses = uploadedFileResponses.filter(response => Boolean(response));

    await new Promise<void>((resolve) => {
      // cancel upload
      if (invalidImageResponses.length > 0) {
        dispatch(showConfirmModal({
          innerContent: feedbackModal,
          confirmButtonLabel: __t('deliverable_media_attachments.button_discard'),
          onConfirm: async () => {
            dispatch(hideConfirmModal());
            await Promise.all(
              invalidImageResponses.map(response =>
                dispatch(finishUploadingDeliverableAttachment(deliverableId, response.tmpAttachment))
              )
            );

            resolve();
          },
          proceedButtonLabel: __t('deliverable_media_attachments.button_upload'),
          onProceed: async () => {
            trackAnalyticScreenshotOverride();
            dispatch(hideConfirmModal());
            // upload invalid images anyway
            await Promise.all(
              invalidImageResponses.map(response =>
                dispatch(assignDeliverableAttachment(
                  offerId,
                  deliverable,
                  attachmentType,
                  response.tmpAttachment,
                  response.parsedMetrics,
                  ignoreMetricsCreation
                ))
              )
            );

            resolve();
          }
        }));
      } else {
        resolve();
      }
    });
  };
}

function attachmentRemoved(offerId: number, deliverableId: number, attachmentId: number | string): ThunkAction {
  return (dispatch) => {
    dispatch(deliverableAttachmentRemoved(offerId, deliverableId, attachmentId));
    dispatch(metricsAttachmentRemoved(deliverableId, attachmentId)); // Report metrics page
    dispatch(campaignDeliverableAttachmentRemoved(deliverableId, attachmentId)); // Brands Content page
  };
}

export function removeDeliverableAttachment(offerId: number, deliverableId: number, attachmentId: number | string): ThunkAction {
  const url = DELIVERABLE_MEDIA.removeOfferAttachment(offerId, deliverableId, attachmentId);

  return async (dispatch) => {
    dispatch(showLoading(url));

    try {
      if (!isNaN(Number(attachmentId))) { // `string` is for tmp files
        await destroy(url);
      }
      dispatch(attachmentRemoved(offerId, deliverableId, attachmentId));
      dispatch(hideLoading(url));
    } catch (error) {
      dispatch(hideLoading(url));
      dispatch(danger(__t('collabary_common.errors.unknown_error')));
      throw error;
    }
  };
}

export function removeDeliverableAttachments(
  offerId: number,
  deliverableId: number,
  attachments: Array<DeliverableAttachment>
): ThunkAction {
  const url = 'removeDeliverableAttachments';

  return async (dispatch) => {
    dispatch(showLoading(url));

    try {
      await Promise.all(attachments.map(attachment =>
        dispatch(removeDeliverableAttachment(offerId, deliverableId, attachment.id))
      ));
      dispatch(hideLoading(url));
    } catch (error) {
      dispatch(hideLoading(url));
      dispatch(danger(__t('collabary_common.errors.unknown_error')));
      throw error;
    }
  };
}

export function createDeliverableAttachmentsBatch(
  offerId: number,
  deliverableId: number,
  attachments: Array<DeliverableAttachment>
): ThunkAction {
  const url = DELIVERABLE_MEDIA.createAttachmentsBatch(offerId, deliverableId);

  return async (dispatch) => {
    dispatch(showLoading(url));

    try {
      const payload = {
        attachments: attachments.map(attachment => omit(attachment, ['id']))
      };
      const response = await post(url, { data: payload });
      await Promise.all(attachments.map(a => dispatch(attachmentRemoved(offerId, deliverableId, a.id))));
      await Promise.all(response.data.map(a => dispatch(attachmentCreated(offerId, deliverableId, a))));
      dispatch(hideLoading(url));
    } catch (error) {
      dispatch(hideLoading(url));
      dispatch(danger(__t('collabary_common.errors.unknown_error')));
      throw error;
    }
  };
}

export function downloadStoryDeliverableAttachments(
  campaignId: number,
  deliverable: Deliverable,
  creatorProfile: SubcampaignInfluencerProfile | undefined | null
): ThunkAction {
  const loaderId = 'downloadStoryDeliverableAttachments()';
  const filename = buildPrefixForDownloadableAttachment(creatorProfile, deliverable.id, deliverable.deliverableType);

  return async (dispatch: Dispatch) => {
    dispatch(showLoading(loaderId));

    const url = CAMPAIGNS.downloadDeliverable(campaignId, deliverable.id, filename);

    try {
      const response = await getRequest(url, {}, { suppressGlobalNotification: true });
      const zipFileS3Url = response?.data.url;
      const jsonFileS3Url = response?.data.statusUrl;

      if (zipFileS3Url && jsonFileS3Url) {
        const presignedURL = await waitUntilZipIsReadyAndGetPresignedURL(jsonFileS3Url);
        await downloadIndividualFile(presignedURL ?? zipFileS3Url);
      } else {
        throw new Error('Returned an empty response');
      }
    } catch (e) {
      if (e?.response?.status === NOT_FOUND_STATUS_CODE) {
        dispatch(danger('Returned an empty response'));
        return;
      }

      logError(e);
    } finally {
      dispatch(hideLoading(loaderId));
    }
  };
}

export function downloadDeliverableAttachments(
  campaignId: number,
  creatorProfile: SubcampaignInfluencerProfile | undefined | null,
  deliverable: Deliverable,
  attachmentType?: string | null,
  approvedOnly?: boolean | null
): ThunkAction {
  const loaderId = 'downloadAttachments()';
  const filename = buildPrefixForDownloadableAttachment(creatorProfile, deliverable.id, deliverable.deliverableType);

  return async (dispatch: Dispatch) => {
    dispatch(showLoading(loaderId));

    const queryString = Querystring.objectToString(snakeCaseKeys({
      creatorId: creatorProfile?.id,
      attachmentType,
      deliverableId: deliverable.id,
      deliverableType: deliverable.deliverableType,
      approvedOnly,
      filename
    }));

    const url = `${CAMPAIGNS.downloadDeliverables(campaignId)}?${queryString}`;

    try {
      const response = await getRequest(url, {}, { suppressGlobalNotification: true });
      const zipFileS3Url = response?.data.url;
      const jsonFileS3Url = response?.data.statusUrl;

      if (zipFileS3Url && jsonFileS3Url) {
        const presignedURL = await waitUntilZipIsReadyAndGetPresignedURL(jsonFileS3Url);
        await downloadIndividualFile(presignedURL ?? zipFileS3Url);
      } else {
        throw new Error('Returned an empty response');
      }
    } catch (e) {
      if (e?.response?.status === NOT_FOUND_STATUS_CODE) {
        dispatch(danger('Returned an empty response'));
        return;
      }

      logError(e);
    } finally {
      dispatch(hideLoading(loaderId));
    }
  };
}

export function uploadDeliverableAttachments(
  files: Array<File>,
  deliverable: Deliverable,
  attachmentType: DeliverableAttachmentType,
  uploadId: string
) {
  return async (dispatch: Dispatch) => {
    const uploadedFileResponses = await Promise.all(
      files.map(file => dispatch(uploadAndVerifyDeliverableAttachment(file, deliverable, attachmentType, undefined, uploadId, true)))
    );

    const transformedResponses = uploadedFileResponses.map((res) => {
      const { tmpAttachment, isInvalidImage, parsedMetrics } = res || {};
      if (res?.tmpAttachment) {
        return {
          ...tmpAttachment,
          parsedBeforeMetrics: (!parsedMetrics || isEmpty(parsedMetrics)) ? null : parsedMetrics,
          isInvalidImage
        };
      }

      return {
        errorMessage: res?.message || res
      };
    });

    return transformedResponses;
  };
}

export default createReducer<any, any>('deliverableMediaAttachments', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [START_UPLOADING]: (state, { deliverableId, tmpAttachment }) => {
    const uploadingAttachments = state.uploadingAttachmentsByDeliverableId[deliverableId] || [];
    return {
      ...state,
      uploadingAttachmentsByDeliverableId: {
        ...state.uploadingAttachmentsByDeliverableId,
        [deliverableId]: [
          ...uploadingAttachments,
          tmpAttachment
        ]
      }
    };
  },
  [FINISH_UPLOADING]: (state, { deliverableId, tmpAttachment }) => {
    const uploadingAttachments = state.uploadingAttachmentsByDeliverableId[deliverableId] || [];

    return {
      ...state,
      uploadingAttachmentsByDeliverableId: {
        ...state.uploadingAttachmentsByDeliverableId,
        [deliverableId]: uploadingAttachments.filter(a => a.id !== tmpAttachment.id)
      }
    };
  },
  [SHOW_ERROR]: (state, { deliverableId, error, attachmentType }) => {
    const errorsByDeliverableId = state.errorsByDeliverableId[deliverableId] || [];
    return {
      ...state,
      errorsByDeliverableId: {
        ...state.errorsByDeliverableId,
        [deliverableId]: [
          ...errorsByDeliverableId,
          { error, attachmentType }
        ]
      }
    };
  },
  [CLEAN_ERRORS]: (state, { deliverableId }) => ({
    ...state,
    errorsByDeliverableId: {
      ...state.errorsByDeliverableId,
      [deliverableId]: []
    }
  }),
  [RESET_UPLOAD_STATE]: (state, { deliverableId }) => ({
    ...state,
    errorsByDeliverableId: {
      ...state.errorsByDeliverableId,
      [deliverableId]: []
    },
    uploadingAttachmentsByDeliverableId: {
      ...state.uploadingAttachmentsByDeliverableId,
      [deliverableId]: []
    }
  })
});
